import React, { useMemo, useCallback } from 'react';
import Button from '@app/react-common/components/button/Button';
import { AbTestWidgetType } from '../../../models/AbTestCreationType';
import { getButtonText } from './abTestModalUtils';
import { getIsThemeWidget } from '../../../utils';
import { ThemeTestDisclaimer } from '../components/ThemeTestDisclaimer/ThemeTestDisclaimer';
interface AbTestModalTrafficAllocationTabProps {
  widgetType: AbTestWidgetType;
  mode: string;
  showInvalidStep: number;
  trafficAllocationValid: boolean;
  fullFormValid: boolean;
  getPreviousTab: (currentTabName: string) => string | null;
  getNextTab: (currentTabName: string) => string | null;
  isNextTabTokenForShopify: (currentTabName: string) => boolean;
  setActiveTab: (tab: string) => void;
  handleSave: () => Promise<void>;
  trafficAllocationContent: React.ReactNode;
  titleText?: string;
  setShowInvalidStep?: (step: number) => void;
  onSaveSuccess?: () => void;
}

export const AbTestModalTrafficAllocationTab: React.FC<AbTestModalTrafficAllocationTabProps> = React.memo(
  ({
    widgetType,
    mode,
    showInvalidStep,
    trafficAllocationValid,
    fullFormValid,
    getPreviousTab,
    getNextTab,
    isNextTabTokenForShopify,
    setActiveTab,
    handleSave,
    trafficAllocationContent,
    titleText,
    setShowInvalidStep,
    onSaveSuccess,
  }) => {
    const defaultTitleText = useMemo(
      () =>
        widgetType === AbTestWidgetType.EXTENSION_GROUPS
          ? "Designate how much traffic you'd like to allocate to each of the test variables"
          : 'Designate how much traffic you’d like to allocate to each theme',
      [widgetType],
    );

    const handleBackClick = useCallback(() => {
      const prevTab = getPreviousTab('Traffic allocation');
      if (prevTab) {
        setActiveTab(prevTab);
      }
    }, [getPreviousTab, setActiveTab]);

    const handleNextClick = useCallback(() => {
      // In view mode, just navigate to next tab without validation or saving
      if (mode === 'view') {
        const nextTab = getNextTab('Traffic allocation');
        if (nextTab) {
          setActiveTab(nextTab);
        } else {
          // No next tab means we're on the last tab, close the modal
          if (onSaveSuccess) {
            onSaveSuccess();
          }
        }
        return;
      }

      if (isNextTabTokenForShopify('Traffic allocation')) {
        if (!fullFormValid) {
          if (setShowInvalidStep) setShowInvalidStep(3);
        } else {
          if (setShowInvalidStep) setShowInvalidStep(0);
          handleSave();
        }
      } else {
        const nextTab = getNextTab('Traffic allocation');
        if (nextTab) {
          if (!trafficAllocationValid) {
            if (setShowInvalidStep) setShowInvalidStep(3);
          } else {
            if (setShowInvalidStep) setShowInvalidStep(0);
            setActiveTab(nextTab);
          }
        } else {
          if (!fullFormValid) {
            if (setShowInvalidStep) setShowInvalidStep(3);
          } else {
            if (setShowInvalidStep) setShowInvalidStep(0);
            handleSave();
          }
        }
      }
    }, [
      mode,
      isNextTabTokenForShopify,
      fullFormValid,
      setShowInvalidStep,
      handleSave,
      getNextTab,
      trafficAllocationValid,
      setActiveTab,
      onSaveSuccess,
    ]);

    const buttonText = useMemo(() => {
      const nextTab = getNextTab('Traffic allocation');
      if (nextTab === 'Token for Shopify' && (mode === 'create' || mode === 'edit' || mode === 'duplicate')) {
        return getButtonText(mode);
      }
      return nextTab ? 'Next' : getButtonText(mode);
    }, [getNextTab, mode]);

    return (
      <>
        <div className={`ab-test-modal__validation ${!trafficAllocationValid && showInvalidStep == 3 ? '' : 'hidden'}`}>
          Please select at least 2 variants and ensure traffic allocation percentages add up to 100%
        </div>
        {getIsThemeWidget(widgetType) && <ThemeTestDisclaimer widgetType={widgetType} />}
        <p className="ab-test-modal__traffic__title">{titleText || defaultTitleText}</p>
        <div className="ab-test-modal__traffic__container">{trafficAllocationContent}</div>
        <div className="ab-test-modal__actions">
          <Button
            onClick={handleBackClick}
            text={'Back'}
            size={'medium'}
            variant={'secondary'}
            className="ab-test-modal__actions__cancel"
          />
          <Button onClick={handleNextClick} text={buttonText} size={'medium'} variant={'primary'} />
        </div>
      </>
    );
  },
);
